import { useEffect, useState, useMemo, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Leaf, Trash2, RefreshCw, Search, CalendarClock, DollarSign, Inbox } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { fetchLeads, updateLeadStatus, deleteLead } from "@/lib/api";
import PricingEditor from "@/pages/PricingEditor";

const TYPE_TABS = ["all", "booking", "quote", "contact", "proposal"];
const STATUS_OPTIONS = ["new", "pending_deposit", "deposit_paid", "contacted", "booked", "archived"];

const TYPE_STYLE = {
  booking: "bg-leaf/15 text-leaf", quote: "bg-navy/10 text-navy",
  contact: "bg-blush/25 text-navy", proposal: "bg-amber-100 text-amber-700",
};
const STATUS_STYLE = {
  new: "bg-blue-100 text-blue-700", pending_deposit: "bg-amber-100 text-amber-700",
  deposit_paid: "bg-leaf/20 text-leaf", contacted: "bg-purple-100 text-purple-700",
  booked: "bg-emerald-100 text-emerald-700", archived: "bg-gray-100 text-gray-500",
};

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white rounded-2xl border border-border p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest mb-2"><Icon className="w-4 h-4 text-leaf" />{label}</div>
    <p className="font-heading text-3xl font-bold text-navy">{value}</p>
  </div>
);

export default function AdminDashboard() {
  const { token, admin, loading: authLoading, logout } = useAdminAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [view, setView] = useState("leads");

  const load = useCallback(async () => {
    setLoading(true);
    try { setLeads(await fetchLeads(token)); }
    catch { toast.error("Could not load leads."); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const filtered = useMemo(() => {
    return leads.filter((l) => (tab === "all" || l.type === tab) &&
      (!q || `${l.name} ${l.email} ${l.phone} ${l.service_type} ${l.address}`.toLowerCase().includes(q.toLowerCase())));
  }, [leads, tab, q]);

  const stats = useMemo(() => ({
    total: leads.length,
    bookings: leads.filter((l) => l.type === "booking").length,
    quotes: leads.filter((l) => l.type === "quote").length,
    deposits: leads.filter((l) => l.deposit_paid).length,
  }), [leads]);

  const onStatus = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try { await updateLeadStatus(token, id, status); toast.success("Status updated"); }
    catch { toast.error("Update failed"); load(); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    setLeads((prev) => prev.filter((l) => l.id !== id));
    try { await deleteLead(token, id); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); load(); }
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-muted"><Loader2 className="w-8 h-8 animate-spin text-leaf" /></div>;
  if (!token) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-muted/40" data-testid="admin-dashboard">
      <header className="bg-navy text-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2"><Leaf className="w-5 h-5 text-leaf" /><span className="font-heading font-semibold tracking-tight">CJB Admin</span></div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/60 hidden sm:block">{admin?.email}</span>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex gap-2 mb-6" data-testid="admin-view-tabs">
          <button onClick={() => setView("leads")} data-testid="view-leads"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${view === "leads" ? "bg-navy text-white" : "bg-white text-navy border border-border hover:border-leaf"}`}>Bookings & Leads</button>
          <button onClick={() => setView("pricing")} data-testid="view-pricing"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${view === "pricing" ? "bg-navy text-white" : "bg-white text-navy border border-border hover:border-leaf"}`}>Pricing</button>
        </div>

        {view === "pricing" ? <PricingEditor token={token} /> : (
        <>
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-heading text-3xl font-bold text-navy">Bookings & Leads</h1>
          <button onClick={load} data-testid="admin-refresh-btn" className="flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-border bg-white text-navy hover:border-leaf transition-colors"><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat icon={Inbox} label="Total Leads" value={stats.total} testid="stat-total" />
          <Stat icon={CalendarClock} label="Bookings" value={stats.bookings} testid="stat-bookings" />
          <Stat icon={Search} label="Quotes" value={stats.quotes} testid="stat-quotes" />
          <Stat icon={DollarSign} label="Deposits Paid" value={stats.deposits} testid="stat-deposits" />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-between mb-5">
          <div className="flex flex-wrap gap-2">
            {TYPE_TABS.map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`filter-${t}`}
                className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-colors ${tab === t ? "bg-navy text-white" : "bg-white text-navy border border-border hover:border-leaf"}`}>{t}</button>
            ))}
          </div>
          <div className="relative sm:w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email…" className="h-10 pl-9 bg-white" />
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-leaf" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground" data-testid="admin-empty">No leads found.</div>
        ) : (
          <div className="bg-white rounded-3xl border border-border overflow-hidden" data-testid="admin-leads-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/60 text-navy/70 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left font-medium px-5 py-3">Contact</th>
                    <th className="text-left font-medium px-5 py-3">Type</th>
                    <th className="text-left font-medium px-5 py-3">Service / Details</th>
                    <th className="text-left font-medium px-5 py-3">Estimate</th>
                    <th className="text-left font-medium px-5 py-3">Deposit</th>
                    <th className="text-left font-medium px-5 py-3">Status</th>
                    <th className="text-left font-medium px-5 py-3">Received</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr key={l.id} className="border-t border-border align-top" data-testid={`lead-row-${l.id}`}>
                      <td className="px-5 py-4">
                        <p className="font-medium text-navy">{l.name}</p>
                        <a href={`mailto:${l.email}`} className="text-muted-foreground hover:text-leaf block">{l.email}</a>
                        {l.phone && <a href={`tel:${l.phone}`} className="text-muted-foreground hover:text-leaf">{l.phone}</a>}
                      </td>
                      <td className="px-5 py-4"><span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${TYPE_STYLE[l.type] || "bg-muted text-navy"}`}>{l.type}</span></td>
                      <td className="px-5 py-4 max-w-[260px]">
                        <p className="text-navy font-medium">{l.service_type || l.service_category || "—"}</p>
                        {l.preferred_date && <p className="text-xs text-muted-foreground">Preferred: {l.preferred_date}</p>}
                        {l.frequency && <p className="text-xs text-muted-foreground">{l.frequency}{l.property_type ? ` · ${l.property_type}` : ""}</p>}
                        {l.address && <p className="text-xs text-muted-foreground">{l.address}</p>}
                        {l.message && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{l.message}</p>}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">{l.estimate_low ? <span className="font-medium text-navy">${l.estimate_low}–${l.estimate_high}</span> : <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-5 py-4">
                        {l.deposit_paid ? <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-leaf/20 text-leaf">Paid $50</span>
                          : l.deposit_required ? <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Pending</span>
                          : <span className="text-muted-foreground text-xs">—</span>}
                      </td>
                      <td className="px-5 py-4">
                        <Select value={l.status} onValueChange={(v) => onStatus(l.id, v)}>
                          <SelectTrigger className={`h-9 w-[150px] border-0 rounded-full text-xs font-medium ${STATUS_STYLE[l.status] || "bg-muted"}`} data-testid={`status-select-${l.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s} className="capitalize text-xs">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-4 text-muted-foreground whitespace-nowrap text-xs">{new Date(l.created_at).toLocaleDateString()}<br />{new Date(l.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="px-5 py-4"><button onClick={() => onDelete(l.id)} data-testid={`delete-lead-${l.id}`} className="text-muted-foreground hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}
